import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '@/hooks/useAppContext';
import type { AIProvider, AIModel } from '@/contexts/AppContext';
import {
  loadProviderSecrets,
  removeProviderSecret,
  setProviderSecret,
} from '@/lib/crypto/secrets';

/** OpenAI-compatible /models response shape */
interface OpenAIModelsResponse {
  object: string;
  data: Array<{
    id: string;
    object: string;
    created?: number;
    owned_by?: string;
  }>;
}

/** Generate a unique provider ID from a name */
export function generateProviderId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + Date.now().toString(36);
}

/**
 * True when the URL points at this machine / LAN — a remote CORS proxy
 * cannot reach it (proxy sees the *server's* localhost, not yours).
 */
export function isLocalProviderUrl(baseUrl: string): boolean {
  if (!baseUrl.trim()) return false;
  try {
    const u = new URL(baseUrl.includes('://') ? baseUrl : `http://${baseUrl}`);
    const host = u.hostname.toLowerCase();
    if (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host === '0.0.0.0' ||
      host.endsWith('.local')
    ) {
      return true;
    }
    // RFC1918 private ranges
    if (/^10\.\d+\.\d+\.\d+$/.test(host)) return true;
    if (/^192\.168\.\d+\.\d+$/.test(host)) return true;
    if (/^172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+$/.test(host)) return true;
    return false;
  } catch {
    return /localhost|127\.0\.0\.1/i.test(baseUrl);
  }
}

/** True when this looks like an Ollama base (local or remote). */
export function isOllamaBaseUrl(baseUrl: string): boolean {
  const lower = baseUrl.toLowerCase();
  return (
    lower.includes('11434') ||
    lower.includes('ollama') ||
    isLocalProviderUrl(baseUrl)
  );
}

/** Effective CORS flag — always false for local URLs. */
export function effectiveUseCorsProxy(baseUrl: string, useCorsProxy: boolean): boolean {
  if (isLocalProviderUrl(baseUrl)) return false;
  return useCorsProxy;
}

/** Build the full API URL, optionally using CORS proxy (never for localhost). */
export function buildApiUrl(baseUrl: string, path: string, useCorsProxy: boolean): string {
  const base = baseUrl.replace(/\/+$/, '');
  const relativePath = path.replace(/^\/+/, '');
  const url = `${base}/${relativePath}`;

  if (!effectiveUseCorsProxy(baseUrl, useCorsProxy)) return url;

  const proxyTemplate =
    localStorage.getItem('opendork:cors-proxy') ||
    'https://proxy.shakespeare.diy/?url={href}';
  return proxyTemplate.replace('{href}', encodeURIComponent(url));
}

/** Strip trailing /v1 so we can hit native Ollama /api/tags. */
export function ollamaRootFromBase(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '').replace(/\/v1$/i, '');
}

/** Candidate base URLs to try for local Ollama (localhost ↔ 127.0.0.1). */
function localBaseVariants(baseUrl: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (u: string) => {
    const n = u.replace(/\/+$/, '');
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  };
  add(baseUrl);
  try {
    const u = new URL(baseUrl.includes('://') ? baseUrl : `http://${baseUrl}`);
    if (u.hostname === 'localhost') {
      u.hostname = '127.0.0.1';
      add(u.toString().replace(/\/$/, ''));
    } else if (u.hostname === '127.0.0.1') {
      u.hostname = 'localhost';
      add(u.toString().replace(/\/$/, ''));
    }
  } catch {
    // ignore
  }
  return out;
}

export function pageIsHttps(): boolean {
  try {
    return typeof location !== 'undefined' && location.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * HTTPS page cannot call http://localhost — browsers treat it as mixed content.
 * (A normal tab to localhost is fine; fetch() from an HTTPS app is not.)
 * Brave is especially strict; Chrome sometimes allows localhost as an exception.
 */
export function isMixedContentLocalBlock(baseUrl: string): boolean {
  return pageIsHttps() && isLocalProviderUrl(baseUrl) && /^http:/i.test(baseUrl.trim());
}

function localReachabilityHint(baseUrl: string): string {
  if (isMixedContentLocalBlock(baseUrl)) {
    return (
      'Blocked by the browser: this page is HTTPS and cannot call http://localhost (mixed content). ' +
      'That is a browser security rule — not OpenDork, CORS, or ad blockers. ' +
      'Local Ollama only works when OpenDork itself is opened over HTTP on this machine (e.g. npm run dev). ' +
      'On the public HTTPS site, use a cloud provider (OpenRouter, OpenAI, Shakespeare AI, …).'
    );
  }
  return (
    'Browser blocked the request (CORS / private-network). If using Ollama on the same machine over HTTP, ' +
    'set OLLAMA_ORIGINS to allow this page’s origin, restart Ollama, then refresh. ' +
    'Opening /api/tags in a tab bypasses CORS — that does not mean the app can fetch it.'
  );
}

/** Short copy for the providers UI when mixed content blocks local Ollama. */
export function mixedContentOllamaHelp(): string {
  return (
    'This deployed site is HTTPS. Browsers refuse to let HTTPS pages call http://localhost ' +
    '(mixed content). Opening Ollama in a separate tab works; fetch from this app does not. ' +
    'There is no setting in OpenDork that can override that.'
  );
}

/**
 * Simple GET without custom headers so browsers skip CORS preflight.
 * Authorization / Content-Type on GET forces OPTIONS — Ollama often fails that.
 */
async function fetchJsonSimple(url: string): Promise<unknown> {
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    // no headers → simple request when possible
  });
  if (!response.ok) {
    let msg = `HTTP ${response.status}: ${response.statusText || 'error'}`;
    try {
      const err = (await response.json()) as { error?: string | { message?: string } };
      if (typeof err.error === 'string') msg = err.error;
      else if (err.error && typeof err.error === 'object' && err.error.message) {
        msg = err.error.message;
      }
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return response.json();
}

function parseOpenAIModels(data: unknown, providerId: string): AIModel[] {
  const body = data as OpenAIModelsResponse;
  if (!body?.data || !Array.isArray(body.data)) {
    throw new Error('Invalid response: expected "data" array from /v1/models');
  }
  return body.data.map((m) => ({
    id: m.id,
    name: m.id,
    providerId,
  }));
}

/** Native Ollama GET /api/tags → { models: [{ name, model, ... }] } */
function parseOllamaTags(data: unknown, providerId: string): AIModel[] {
  const body = data as { models?: Array<{ name?: string; model?: string }> };
  if (!body?.models || !Array.isArray(body.models)) {
    throw new Error('Invalid Ollama /api/tags response');
  }
  return body.models
    .map((m) => {
      const id = m.name || m.model;
      if (!id) return null;
      return { id, name: id, providerId };
    })
    .filter((m): m is AIModel => m !== null);
}

/** Fetch models — tries OpenAI /v1/models then Ollama /api/tags; avoids preflight on local. */
export async function fetchProviderModels(provider: AIProvider): Promise<AIModel[]> {
  // Don't even attempt — browser will always fail HTTPS page → http://localhost
  if (isMixedContentLocalBlock(provider.baseUrl)) {
    throw new Error(localReachabilityHint(provider.baseUrl));
  }

  const useProxy = effectiveUseCorsProxy(provider.baseUrl, provider.useCorsProxy);
  const local = isLocalProviderUrl(provider.baseUrl);
  const bases = local ? localBaseVariants(provider.baseUrl) : [provider.baseUrl.replace(/\/+$/, '')];

  const errors: string[] = [];

  for (const base of bases) {
    // 1) OpenAI-compatible /v1/models
    const modelsUrl = buildApiUrl(base, 'models', useProxy);
    try {
      if (local || isOllamaBaseUrl(base)) {
        // Prefer no-header GET for local (avoids preflight)
        const data = await fetchJsonSimple(modelsUrl);
        return parseOpenAIModels(data, provider.id);
      }
      // Cloud providers: auth header required
      const response = await fetch(modelsUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${provider.apiKey || 'ollama'}`,
        },
      });
      if (!response.ok) {
        let msg = `HTTP ${response.status}`;
        try {
          const err = await response.json();
          msg = err.error?.message || err.message || msg;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }
      return parseOpenAIModels(await response.json(), provider.id);
    } catch (err) {
      errors.push(`/v1/models @ ${base}: ${err instanceof Error ? err.message : String(err)}`);
    }

    // 2) Native Ollama /api/tags (works when OpenAI shim is flaky)
    if (local || isOllamaBaseUrl(base)) {
      const tagsUrl = `${ollamaRootFromBase(base)}/api/tags`;
      try {
        const data = await fetchJsonSimple(tagsUrl);
        const models = parseOllamaTags(data, provider.id);
        if (models.length === 0) {
          throw new Error('Ollama returned zero models — run: ollama pull <model>');
        }
        return models;
      } catch (err) {
        errors.push(`/api/tags @ ${tagsUrl}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }

  if (local) {
    throw new Error(
      `Could not list models from Ollama (${provider.baseUrl}). ` +
        localReachabilityHint(provider.baseUrl) +
        (errors.length ? ` Details: ${errors[errors.length - 1]}` : ''),
    );
  }

  throw new Error(
    `Network error listing models. ` +
      (useProxy
        ? 'CORS proxy may have failed.'
        : 'Try enabling "Use CORS proxy" if the provider blocks browser requests. ') +
      (errors[0] ?? ''),
  );
}

/** Parse free-text model list (one per line or comma-separated). */
export function parseManualModelList(text: string, providerId: string): AIModel[] {
  const parts = text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: AIModel[] = [];
  for (const id of parts) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: id, providerId });
  }
  return out;
}

/** Send a chat completion to a custom provider */
export async function sendChatCompletion(
  provider: AIProvider,
  modelId: string,
  messages: Array<{ role: string; content: string | unknown }>,
  options?: {
    temperature?: number;
    max_tokens?: number;
    stream?: boolean;
    signal?: AbortSignal;
  }
): Promise<Response> {
  const url = buildApiUrl(
    provider.baseUrl,
    'chat/completions',
    effectiveUseCorsProxy(provider.baseUrl, provider.useCorsProxy),
  );
  const { signal, ...bodyOptions } = options ?? {};

  // Local Ollama: omit Authorization when no real key — still needs Content-Type
  // (POST always preflights). Cloud providers keep Bearer auth.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const key = provider.apiKey?.trim();
  if (key && key !== 'ollama') {
    headers.Authorization = `Bearer ${key}`;
  } else if (!isLocalProviderUrl(provider.baseUrl)) {
    headers.Authorization = `Bearer ${key || 'ollama'}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelId,
      messages,
      ...bodyOptions,
    }),
    signal,
  });

  if (!response.ok) {
    let msg = `API error: ${response.statusText}`;
    try {
      const err = await response.json();
      msg = err.error?.message || err.message || msg;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }

  return response;
}

/** Strip apiKey from config-stored provider (keys live in sealed secrets). */
function publicProvider(p: AIProvider): AIProvider {
  return { ...p, apiKey: '' };
}

/** Hook for managing custom AI providers */
export function useAIProviders() {
  const { config, updateConfig } = useAppContext();
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [secretsReady, setSecretsReady] = useState(false);

  // Load sealed secrets + migrate legacy plaintext keys from config once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let loaded = await loadProviderSecrets();
      const legacy = config.aiProviders ?? [];
      let migrated = false;
      for (const p of legacy) {
        if (p.apiKey && !loaded[p.id]) {
          loaded = { ...loaded, [p.id]: p.apiKey };
          migrated = true;
        }
      }
      if (migrated) {
        await import('@/lib/crypto/secrets').then((m) => m.saveProviderSecrets(loaded));
        // Clear plaintext keys from app config
        updateConfig((c) => ({
          ...c,
          aiProviders: (c.aiProviders ?? []).map((p) => publicProvider(p)),
        }));
      }
      if (!cancelled) {
        setSecrets(loaded);
        setSecretsReady(true);
      }
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
    // Only on mount / when provider list identity changes count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload secrets when vault may have unlocked (poll lightly via focus)
  useEffect(() => {
    const reload = () => {
      void loadProviderSecrets().then(setSecrets);
    };
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, []);

  // Stable reference when config providers + secrets unchanged (avoids
  // infinite model-fetch loops from new array identity every render).
  const providers: AIProvider[] = useMemo(
    () =>
      (config.aiProviders ?? []).map((p) => ({
        ...p,
        apiKey: secrets[p.id] || p.apiKey || '',
      })),
    [config.aiProviders, secrets],
  );

  const addProvider = useCallback((provider: Omit<AIProvider, 'id' | 'models'> & { id?: string; models?: AIModel[] }) => {
    const id = provider.id || generateProviderId(provider.name);
    // Ollama etc. accept empty key — store a placeholder so fetch Authorization still works
    const apiKey = provider.apiKey?.trim() || 'ollama';
    const newProvider: AIProvider = {
      ...provider,
      id,
      apiKey: '',
      models: provider.models || [],
    };
    updateConfig((c) => ({
      ...c,
      aiProviders: [...(c.aiProviders ?? []), newProvider],
    }));
    void setProviderSecret(id, apiKey)
      .then(() => setSecrets((s) => ({ ...s, [id]: apiKey })))
      .catch((err) => {
        console.error('Failed to store API key', err);
      });
    return { ...newProvider, apiKey };
  }, [updateConfig]);

  const updateProvider = useCallback((id: string, updates: Partial<AIProvider>) => {
    const { apiKey, ...rest } = updates;
    updateConfig((c) => ({
      ...c,
      aiProviders: (c.aiProviders ?? []).map((p) =>
        p.id === id ? { ...p, ...rest, apiKey: '' } : p
      ),
    }));
    if (apiKey !== undefined) {
      void setProviderSecret(id, apiKey)
        .then(() =>
          setSecrets((s) => {
            const next = { ...s };
            if (apiKey) next[id] = apiKey;
            else delete next[id];
            return next;
          }),
        )
        .catch((err) => {
          console.error('Failed to store API key', err);
        });
    }
  }, [updateConfig]);

  const removeProvider = useCallback((id: string) => {
    updateConfig((c) => ({
      ...c,
      aiProviders: (c.aiProviders ?? []).filter((p) => p.id !== id),
      defaultAiProvider: c.defaultAiProvider === id ? undefined : c.defaultAiProvider,
    }));
    void removeProviderSecret(id);
    setSecrets((s) => {
      const next = { ...s };
      delete next[id];
      return next;
    });
  }, [updateConfig]);

  const setDefaultProvider = useCallback((id: string | undefined) => {
    updateConfig((c) => ({ ...c, defaultAiProvider: id }));
  }, [updateConfig]);

  const refreshModels = useCallback(async (providerId: string): Promise<AIModel[]> => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) throw new Error('Provider not found');

    const models = await fetchProviderModels(provider);
    updateProvider(providerId, { models, modelsFetchedAt: Date.now() });
    return models;
  }, [providers, updateProvider]);

  const getAllModels = useCallback((): AIModel[] => {
    return providers.flatMap((p) => p.models ?? []);
  }, [providers]);

  const getProvider = useCallback((id: string): AIProvider | undefined => {
    return providers.find((p) => p.id === id);
  }, [providers]);

  return {
    providers,
    secretsReady,
    defaultProvider: config.defaultAiProvider,
    addProvider,
    updateProvider,
    removeProvider,
    setDefaultProvider,
    refreshModels,
    getAllModels,
    getProvider,
    reloadSecrets: () => loadProviderSecrets().then(setSecrets),
  };
}
